import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { WaardelijstenApi } from "../../../api/waardelijsten";
import { IdentifierWithChildren } from "../../../factories/waardelijst";
import { IdentifierMetAfkorting } from "../../../models/identifier";
import { SearchResult } from "../../../models/search-result";
import { SortDirection, uploadTypes } from "../../../models/sorting";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { conceptDate, publiceerConcept } from "../../../store/reducers/document-metadata";
import {
  clearSearch,
  clearSelectedConcepts,
  searchBijlages,
  searchDocuments,
  updateFilter,
  updateSearch,
  updateSelectedConcepts,
  updateSorting,
} from "../../../store/reducers/search";
import { selectSearch } from "../../../store/selectors";
import { formatStringToDate } from "../../../utils/DateFormatter";
import { isBijlageDocument } from "../../../utils/Relaties";
import { StringSanitizer } from "../../../utils/StringSanitizer";
import { WaardelijstCrawler } from "../../../utils/WaardelijstCrawler";
import {
  CheckboxAtom,
  ContainerAtom,
  DataTableAtom,
  HeadingH2Atom,
  IconTextCellAtom,
  SpacerAtom,
  StatusBadgeAtom,
} from "../../atoms";
import { Column } from "../../atoms/data-table";
import { StatusBadgeColor } from "../../atoms/status-badge";
import { TypographyAtom } from "../../atoms/typography";
import { IToggle } from "../../interfaces/Toggle";
import {
  ButtonMolecule,
  ButtonWithMenuMolecule,
  PaginationMolecule,
  PopupDepublicerenDocumentMolecule,
  PopupDepublicerenDossierMolecule,
  RadioInputMolecule,
  SearchInputMolecule,
  ToggleRowMolecule,
  VerwerkingsoverzichtBannerMolecule,
} from "../../molecules";
import styles from "./styles.module.scss";

export interface DocumentRow {
  id: string;
  titel: string;
  type: uploadTypes;
  onderdelen?: number;
  laatstBewerkt?: string;
  gepubliceerdVanaf?: string;
  identificatieKenmerk: string[];
  organisatie: string;
  status?: string;
  hasChildren?: boolean;
  hoofddocumentId?: string;
  searchResult: SearchResult;
}

const stringSanitizer = new StringSanitizer();

export function DocumentenLijstOrganism() {
  const stateSearch = useAppSelector(selectSearch);
  const containerRef = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const waardelijstFactory = new WaardelijstenApi();

  const [popupDepubliceren, setPopupDepubliceren] = useState<
    { id: string; type: uploadTypes; hoofddocumentId?: string } | undefined
  >(undefined);
  const [page, setPage] = useState(1);
  const [ministeries, setMinisteries] = useState<IdentifierWithChildren[]>([]);
  const [overigeOrganisaties, setOverigeOrganisaties] = useState<IdentifierWithChildren[]>([]);
  const [samenwerkingOrganisaties, setSamenwerkingOrganisaties] = useState<IdentifierWithChildren[]>([]);
  const [zelfstandigeBestuursorganen, setZelfstandigeBestuursorganen] = useState<IdentifierWithChildren[]>([]);
  const [publicerendePartijen, setPublicerendePartijen] = useState<IdentifierMetAfkorting[]>([]);
  const [sort, setSort] = useState<{ key: keyof DocumentRow; direction: SortDirection }>({
    key: "laatstBewerkt",
    direction: "desc",
  });

  const getIconChild = (result: SearchResult) => {
    const isBijlage = result.documentrelaties ? isBijlageDocument(result.documentrelaties) : false;
    if (isBijlage) return "paperclip";
    return "icon-document";
  };

  const selectionColumn: Column<DocumentRow> = {
    key: "selection",
    label: "",
    width: "55px",
    render: (row, parentRow) => {
      if (parentRow) return null;
      if (row.status !== "Concept") return null;

      const result: SearchResult | undefined = searchResultByPid.get(row.id);

      return (
        <CheckboxAtom
          id={"concept_checkbox_" + row.id}
          disabled={result === undefined}
          checked={result ? stateSearch.selectedConcepts.includes(result) : false}
          aria-label={`Selecteer ${row.titel}`}
          label={""}
          onClick={(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
            event.stopPropagation();
            if (result) dispatch(updateSelectedConcepts(result));
          }}
        />
      );
    },
  };

  const baseColumns: Column<DocumentRow>[] = [
    {
      key: "titel",
      label: "Titel",
      sortable: true,
      width: stateSearch.filters === "concept" ? "17.4%" : "21%",
      render: (row: DocumentRow, parent?: DocumentRow) => {
        const icon = getIconChild(row.searchResult);

        return <IconTextCellAtom icon={icon}>{row.titel}</IconTextCellAtom>;
      },
    },
    {
      key: "type",
      label: "Type",
      width: "7%",
    },
    {
      key: "onderdelen",
      label: "Onderdelen",
      width: "7%",
      render: (row: DocumentRow) => (typeof row.onderdelen === "number" && row.onderdelen >= 0 ? row.onderdelen : null),
    },
    { key: "laatstBewerkt", label: "Laatst bewerkt", sortable: true, width: "10%" },
    { key: "gepubliceerdVanaf", label: "Gepubliceerd vanaf", sortable: true, width: "12%" },
    { key: "identificatieKenmerk", label: "Identificatie kenmerk", width: "12%" },
    { key: "organisatie", label: "Organisatie", width: "15%" },
    {
      key: "status",
      label: "Status",
      width: "11%",
      render: (row: DocumentRow) => {
        if (!row.status) return <></>;

        const config = STATUS_CONFIG[row.status];

        if (!config) {
          return row.status;
        }

        return <StatusBadgeAtom label={config.label} icon={config.icon} color={config.color} />;
      },
    },
    {
      key: "actions",
      label: "Acties",
      width: "5%",
      render: (row: DocumentRow) => (
        <ButtonWithMenuMolecule
          rowId={row.id}
          options={[
            {
              label: "Bewerken",
              onClick: () => navigateToUpdate(row.id),
              icon: "square-pen",
              disabled: false,
            },
            {
              label: "Intrekken",
              onClick: () => setPopupDepubliceren({ id: row.id, type: row.type, hoofddocumentId: row.hoofddocumentId }),
              icon: "icon-remove-blue",
              disabled: row.type === "Dossier" || row.status === "Ingetrokken" || row.status === "Pending",
            },
            {
              label: "Portaal",
              onClick: () => window.open(`${process.env.REACT_APP_PORTAAL_URL}/details/${row.id}`, "_blank"),
              icon: "Zichtbaar",
              disabled: false,
            },
          ]}
        />
      ),
    },
  ];

  const searchResultByPid = useMemo(() => {
    const map = new Map<string, SearchResult>();
    stateSearch.data.resultaten.forEach((r) => {
      if (r.document.pid) map.set(stringSanitizer.extractIdentifierFromPid(r.document.pid), r);
    });
    return map;
  }, [stateSearch.data.resultaten]);

  const columns: Column<DocumentRow>[] = useMemo(() => {
    return stateSearch.filters === "concept" ? [selectionColumn, ...baseColumns] : baseColumns;
  }, [stateSearch.filters, searchResultByPid, stateSearch.selectedConcepts]);

  useMemo(() => {
    const partijen: IdentifierMetAfkorting[] = ministeries
      .concat(overigeOrganisaties, zelfstandigeBestuursorganen, samenwerkingOrganisaties)
      .map((partij: IdentifierWithChildren) => {
        const afkorting: IdentifierMetAfkorting = WaardelijstCrawler.searchOrganisationAfkorting({
          id: partij.id,
          label: partij.label,
        });
        return {
          id: partij.id,
          label: partij.label,
          afkorting: afkorting.afkorting ?? undefined,
        };
      });

    setPublicerendePartijen(partijen);
  }, [ministeries, overigeOrganisaties, zelfstandigeBestuursorganen, samenwerkingOrganisaties]);

  useEffect(() => {
    waardelijstFactory.getOverigeOrganisaties().then(setOverigeOrganisaties);
    waardelijstFactory.getMinisterie().then(setMinisteries);
    waardelijstFactory.getZelfstandigeBestuurorganen().then(setZelfstandigeBestuursorganen);
    waardelijstFactory.getSamenwerkingsOrganisaties().then(setSamenwerkingOrganisaties);
  }, [dispatch]);

  useEffect(() => {
    handleSort("laatstBewerkt", "desc");
  }, []);

  const navigateToUpdate = (id: string) => {
    navigate(`/aanleveren/${id}`);
  };

  const scrollToTop = () => {
    const container = document.querySelector<HTMLElement>('[class*="layouts__page__body__overflow_auto"]');
    if (container) {
      container.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const search = async (pagina: number) => {
    if (stateSearch.fetching) return;
    setPage(pagina);
    await dispatch(searchDocuments({ pagina: pagina }));
    scrollToTop();
  };

  const updateList = async (pagina: number) => {
    dispatch(clearSelectedConcepts());
    dispatch(clearSearch());
    setPage(pagina);
    await dispatch(searchDocuments({ pagina: pagina }));
  };

  const getSearchResultVariables = (
    result: SearchResult
  ): {
    org: string;
    title: string;
    gepubliceerdVanaf: string | undefined;
    isConcept: boolean;
    status: string;
    identifiers: string[];
  } => {
    const organisatie = publicerendePartijen.find(
      (organisatie: IdentifierMetAfkorting) => organisatie.label === result.document.publisher.label
    );
    const org = organisatie ? (organisatie?.afkorting ?? organisatie?.label) : "";
    const title = result.document.titelcollectie.officieleTitel;
    const gepubliceerdVanaf = result.document?.zichtbaarheidsdatumtijd
      ? result.document?.zichtbaarheidsdatumtijd
      : result.versies?.at(0)?.openbaarmakingsdatum;
    const isNotIntrekking = result.plooiIntern?.publicatiestatus !== "intrekking";
    const isConcept = new Date(gepubliceerdVanaf ?? "").toJSON() === conceptDate.toJSON() && isNotIntrekking;
    const status = isConcept
      ? "Concept"
      : stringSanitizer.sanitizePublicatieStatus(result.plooiIntern?.publicatiestatus ?? "");
    const identifiers = result.document.identifiers;
    return { org, title, gepubliceerdVanaf, isConcept, status, identifiers };
  };

  const onUpdateSearch = (search: string) => {
    dispatch(updateSearch(search));
  };

  const toggles = [
    {
      id: "boomweergave",
      label: "Boomweergave",
      checked: stateSearch.filters === "hoofddocument" || stateSearch.filters === "concept",
      onClick: () => {
        if (stateSearch.search !== "") onUpdateSearch("");

        dispatch(updateFilter("hoofddocument"));
        updateList(1);
      },
    } as IToggle,
    {
      id: "lijstweergave",
      label: "Lijstweergave",
      checked: stateSearch.filters === undefined,
      onClick: () => {
        dispatch(updateFilter(undefined));
        updateList(1);
      },
    } as IToggle,
  ];

  const STATUS_CONFIG: Record<string, { label: string; icon: string; color: StatusBadgeColor }> = {
    Concept: {
      label: "Concept",
      icon: "square-pen",
      color: "info",
    },
    "Latere-publicatie": {
      label: "Gepland",
      icon: "Tijd",
      color: "pending",
    },
    Gepubliceerd: {
      label: "Gepubliceerd",
      icon: "check",
      color: "success",
    },
    Ingetrokken: {
      label: "Ingetrokken",
      icon: "icon-remove-blue",
      color: "neutral",
    },
  };

  const mapToRow = (result: SearchResult, rowClick: boolean = false, hoofddocumentId?: string): DocumentRow => {
    const { org, title, gepubliceerdVanaf, isConcept, status, identifiers } = getSearchResultVariables(result);
    const laatstBewerkt = result.versies?.at(0)?.mutatiedatumtijd
      ? formatStringToDate(result.versies?.at(0)?.mutatiedatumtijd!)
      : undefined;
    const childrenLength = result.documentrelaties?.length;
    const onderdelen = childrenLength ?? 0;

    return {
      id: stringSanitizer.extractIdentifierFromPid(result.document.pid),
      titel: title,
      type: "Document",
      onderdelen: rowClick ? undefined : onderdelen,
      laatstBewerkt: laatstBewerkt,
      gepubliceerdVanaf: isConcept || !gepubliceerdVanaf ? undefined : formatStringToDate(gepubliceerdVanaf),
      identificatieKenmerk: identifiers,
      organisatie: org,
      status: isConcept ? "Concept" : status,
      hasChildren: !!childrenLength,
      hoofddocumentId,
      searchResult: result,
    };
  };

  let rows: DocumentRow[] = stateSearch.data
    ? stateSearch.data.resultaten.map((result: SearchResult) => mapToRow(result))
    : [];

  const handleRowClick = async (rowId: string, rowIndex: number) => {
    if (!stateSearch.fetchingBijlage) {
      const results = await dispatch(searchBijlages({ uuid: rowId, rowIndex })).unwrap();
      const relations: DocumentRow[] = results.foldableContent.map((row: SearchResult) => mapToRow(row, true, rowId));
      return relations;
    }
  };

  const handleSort = async (key: keyof DocumentRow, direction: SortDirection) => {
    setSort({ key, direction });

    switch (key) {
      case "titel":
        dispatch(updateSorting({ sortBy: "titel", sortDirection: direction }));
        await dispatch(searchDocuments({ pagina: page }));
        break;

      case "laatstBewerkt":
        dispatch(updateSorting({ sortBy: "laatst-bewerkt", sortDirection: direction }));
        await dispatch(searchDocuments({ pagina: page }));
        break;

      case "gepubliceerdVanaf":
        dispatch(updateSorting({ sortBy: "gepubliceerd-vanaf", sortDirection: direction }));
        await dispatch(searchDocuments({ pagina: page }));
        break;

      default:
        return;
    }
  };

  const renderPopupDepubliceren = () => {
    if (!popupDepubliceren) return null;

    if (popupDepubliceren.type === "Dossier") {
      return (
        <PopupDepublicerenDossierMolecule
          depublicerenId={popupDepubliceren.id}
          close={() => setPopupDepubliceren(undefined)}
        />
      );
    }
    return (
      <PopupDepublicerenDocumentMolecule
        depublicerenId={popupDepubliceren.id}
        hoofddocumentId={popupDepubliceren.hoofddocumentId}
        close={() => setPopupDepubliceren(undefined)}
      />
    );
  };

  return (
    <div ref={containerRef}>
      {process.env.REACT_APP_VERWERKINGSOVERZICHT === "true" && <VerwerkingsoverzichtBannerMolecule />}
      <ContainerAtom type="row" direction="row">
        <ContainerAtom type="between" direction="column" width={"190%"}>
          <>
            <HeadingH2Atom>Verwerkt</HeadingH2Atom>
            <TypographyAtom>
              {[
                "Hier vindt u een overzicht van alle documenten die zijn gepubliceerd, als concept zijn opgeslagen, gepland staan of zijn ingetrokken.",
              ]}
            </TypographyAtom>
            <SpacerAtom space={4} />
            <ContainerAtom type="flex" direction="row">
              <ToggleRowMolecule id={"lijstOptions"} toggles={toggles} startIndex={0} />
              <ContainerAtom type="flex" direction="row">
                <SpacerAtom space={8} type="horizontal" />
                <b>Tonen:</b>
                <SpacerAtom space={4} type="horizontal" />
                <RadioInputMolecule
                  id={"Alles"}
                  onChange={() => {
                    if (stateSearch.search !== "") onUpdateSearch("");
                    dispatch(updateFilter("hoofddocument"));
                    updateList(1);
                  }}
                  placeholder={"Alle statussen"}
                  value={stateSearch.filters === "concept" ? undefined : "true"}
                />
                <SpacerAtom space={4} type="horizontal" />
                <RadioInputMolecule
                  id={"Concepten"}
                  onChange={() => {
                    if (stateSearch.search !== "") onUpdateSearch("");
                    dispatch(updateFilter("concept"));
                    updateList(1);
                  }}
                  placeholder={"Concepten"}
                  value={stateSearch.filters === "concept" ? "true" : undefined}
                />
              </ContainerAtom>
            </ContainerAtom>
          </>
        </ContainerAtom>
        <div className={styles.flexEnd}>
          <ContainerAtom type="flex" direction="row">
            <ContainerAtom type="row" direction="row" width={"200px"} height={"58px"}>
              {stateSearch.filters === "concept" && (
                <ButtonMolecule
                  text={"Publiceren"}
                  id={"publiceren"}
                  title={"Publiceer concepten."}
                  type={"primary"}
                  disabled={stateSearch.selectedConcepts.length === 0}
                  onClick={async () => {
                    await dispatch(publiceerConcept(stateSearch.selectedConcepts));
                    updateList(page);
                  }}
                />
              )}
            </ContainerAtom>
            <SpacerAtom space={8} type="horizontal" />
            <SearchInputMolecule
              value={stateSearch.search}
              onSubmit={() => {
                dispatch(updateFilter(undefined));
                updateList(1);
              }}
              onChange={onUpdateSearch}
            />
          </ContainerAtom>
        </div>
      </ContainerAtom>

      <SpacerAtom space={4} />
      <DataTableAtom
        columns={columns}
        data={rows}
        sort={sort}
        onSort={handleSort}
        loadChildren={stateSearch.filters === undefined ? undefined : handleRowClick}
      />
      <SpacerAtom space={4} />
      <PaginationMolecule
        currentPage={page}
        totalPages={Math.ceil(stateSearch.data.totaal / 25)}
        onPageChange={(newPage) => {
          search(newPage);
        }}
      />
      {renderPopupDepubliceren()}
    </div>
  );
}
